import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js/max';

/**
 * Formato armazenado do telefone (contrato com o back, PhoneNumberUtils do sociotorcedor-client):
 * - Brasil: apenas dígitos nacionais (DDD + número), sem o 55. Ex.: 85999998888
 * - Outros países: "+" + DDI + número nacional, só dígitos após o "+". Ex.: +351912345678
 * - Vazio: ''.
 *
 * ATENÇÃO: arquivo idêntico em sociotorcedor-clube-web (src/app/shared/data/phone-format.ts) e
 * sociotorcedor-web (src/app/core/phone/phone-format.ts). Altere os dois juntos.
 */

export type PhoneKind = 'mobile' | 'any';

export interface ParsedPhone {
  country: CountryCode;
  national: string;
}

const BRAZIL: CountryCode = 'BR';
const BRAZIL_CALLING_CODE = '55';
/** E.164: no máximo 15 dígitos somando DDI e número nacional. */
export const E164_MAX_DIGITS = 15;
const MAX_CALLING_CODE_LENGTH = 3;

/** País "principal" de DDIs compartilhados, usado quando o número ainda não define o país. */
const MAIN_COUNTRY_BY_CALLING_CODE: Record<string, CountryCode> = {
  '1': 'US',
  '7': 'RU',
  '39': 'IT',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW'
};

let countryByCallingCode: Map<string, CountryCode> | null = null;

function getCountryByCallingCode(): Map<string, CountryCode> {
  if (!countryByCallingCode) {
    const table = new Map<string, CountryCode>();
    for (const iso2 of getCountries()) {
      const code = getCountryCallingCode(iso2);
      if (!table.has(code)) {
        table.set(code, iso2);
      }
    }
    Object.keys(MAIN_COUNTRY_BY_CALLING_CODE).forEach(code => table.set(code, MAIN_COUNTRY_BY_CALLING_CODE[code]));
    countryByCallingCode = table;
  }
  return countryByCallingCode;
}

export function onlyDigits(value: string | null | undefined): string {
  return (value || '').replace(/\D/g, '');
}

/**
 * Entrada digitada/colada em formato internacional, com a mesma regra do back (PhoneNumberUtils.normalize): "+..." ou
 * prefixo de discagem internacional "00" + DDI conhecido. Só os dígitos contam (máscara como "(00) 351" também vale).
 * Devolve os dígitos após o "+"/"00", ou null se a entrada não é internacional. "0 21 ..." (operadora BR) não é "00".
 */
export function internationalDigits(raw: string | null | undefined): string | null {
  const value = (raw || '').trim();
  if (value.startsWith('+')) {
    return onlyDigits(value);
  }
  const digits = onlyDigits(value);
  if (digits.startsWith('00') && hasKnownCallingCode(digits.slice(2))) {
    return digits.slice(2);
  }
  return null;
}

/** DDI conhecido no início dos dígitos, inclusive os não geográficos (ex.: 800), como no back. */
function hasKnownCallingCode(digits: string): boolean {
  return splitByCallingCode(digits) !== null || !!parsePhoneNumberFromString(`+${digits}`)?.countryCallingCode;
}

/**
 * Separa o DDI do início dos dígitos. Procura do prefixo mais longo para o mais curto (os DDIs E.164 não são
 * prefixo uns dos outros, então o resultado é o mesmo, mas sem depender disso).
 */
function splitByCallingCode(digits: string): ParsedPhone | null {
  const table = getCountryByCallingCode();
  for (let size = Math.min(MAX_CALLING_CODE_LENGTH, digits.length); size >= 1; size--) {
    const country = table.get(digits.slice(0, size));
    if (country) {
      return { country, national: digits.slice(size, E164_MAX_DIGITS) };
    }
  }
  return null;
}

/** Número nacional sem prefixo de tronco (ex.: "0" do Reino Unido), mesmo quando parcial. */
function nationalWithoutTrunkPrefix(country: CountryCode, digits: string): string {
  const parser = new AsYouType(country);
  parser.input(digits);
  const number = parser.getNumber();
  return number ? number.nationalNumber.toString() : digits;
}

/** País + número nacional (sem prefixos) -> formato armazenado ('' se vazio). */
export function buildStoredPhone(country: CountryCode, national: string): string {
  const digits = onlyDigits(national);
  if (!digits) {
    return '';
  }
  if (country === BRAZIL) {
    return digits;
  }
  const code = getCountryCallingCode(country);
  return `+${code}${digits.slice(0, E164_MAX_DIGITS - code.length)}`;
}

/** Telefone armazenado -> país + número nacional. Sem "+" é Brasil (aceita o legado com 55 na frente). */
export function parseStoredPhone(stored: string | null | undefined): ParsedPhone {
  const value = (stored || '').trim();
  if (!value) {
    return { country: BRAZIL, national: '' };
  }

  if (value.startsWith('+')) {
    return detectCountryFromInput(value) || { country: BRAZIL, national: onlyDigits(value) };
  }

  let digits = onlyDigits(value);
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith(BRAZIL_CALLING_CODE)) {
    digits = digits.slice(BRAZIL_CALLING_CODE.length);
  }
  return { country: BRAZIL, national: digits };
}

/**
 * Entrada internacional ("+..." ou "00...") -> país detectado pelo DDI/número + número nacional.
 * Devolve null se a entrada não é internacional ou o DDI ainda é desconhecido.
 */
export function detectCountryFromInput(raw: string): ParsedPhone | null {
  const digits = internationalDigits(raw);
  if (!digits) {
    return null;
  }

  const parsed = parsePhoneNumberFromString(`+${digits}`);
  if (parsed && parsed.country) {
    return { country: parsed.country, national: parsed.nationalNumber.toString() };
  }
  return splitByCallingCode(digits);
}

/** País selecionado + texto digitado -> telefone armazenado ('' se vazio). Entrada internacional prevalece. */
export function toStoredPhone(country: CountryCode, national: string): string {
  const intl = internationalDigits(national);
  if (intl !== null) {
    if (!intl) {
      return '';
    }
    const detected = detectCountryFromInput(national);
    // DDI desconhecido: mantém como digitado para a validação acusar, em vez de trocar pelo país selecionado
    return detected ? buildStoredPhone(detected.country, detected.national) : `+${intl.slice(0, E164_MAX_DIGITS)}`;
  }

  const digits = onlyDigits(national);
  if (!digits) {
    return '';
  }
  if (country === BRAZIL) {
    // Remove 55 e prefixo de tronco/operadora (ex.: "0", "0 21"), que o back rejeita
    const parsed = parsePhoneNumberFromString(digits, BRAZIL);
    return parsed ? parsed.nationalNumber.toString() : digits;
  }
  return buildStoredPhone(country, nationalWithoutTrunkPrefix(country, digits));
}

/** Exibição: Brasil em formato nacional "(85) 99999-8888"; estrangeiro internacional "+351 912 345 678". */
export function formatStoredPhone(stored: string | null | undefined): string {
  const { country, national } = parseStoredPhone(stored);
  if (!national) {
    return '';
  }

  if (country === BRAZIL) {
    const parsed = parsePhoneNumberFromString(national, BRAZIL);
    return parsed ? parsed.formatNational() : national;
  }

  const storedPhone = buildStoredPhone(country, national);
  const parsed = parsePhoneNumberFromString(storedPhone);
  return parsed ? parsed.formatInternational() : storedPhone;
}

/** Motivo da invalidez: 'invalid' (não existe no país), 'notMobile' (válido, mas não é celular) ou null (válido). */
export function phoneValidationError(
  country: CountryCode,
  national: string,
  kind: PhoneKind
): 'invalid' | 'notMobile' | null {
  const digits = onlyDigits(national);
  if (!digits) {
    return 'invalid';
  }

  const parsed = parsePhoneNumberFromString(digits, country);
  if (!parsed || !parsed.isValid()) {
    return 'invalid';
  }
  if (kind === 'any') {
    return null;
  }

  // Quando o tipo não pode ser determinado, aceita.
  const type = parsed.getType();
  return !type || type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE' ? null : 'notMobile';
}
